import { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Download,
  Home,
  Info,
  Landmark,
  LineChart,
  ShieldCheck,
  TrendingUp,
  Wallet,
  WalletCards,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import "../styles/pages/relatorios.css";
import {
  getResumoPeriodo,
  getUltimasTransacoes,
  getTopCategorias,
  getInvestimentosPeriodo,
} from "../services/financeService";

const COLORS = ["#22c55e", "#38bdf8", "#3b82f6", "#fb923c", "#ef4444", "#64748b"];
const KPI_COLORS = {
  entradas: "#22c55e",
  saidas: "#ef4444",
  saldo: "#3b82f6",
  investimentos: "#c084fc",
  poupanca: "#fbbf24",
};

function moeda(valor) {
  return Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function numero(valor) {
  return Number(valor || 0).toLocaleString("pt-BR");
}

function mesParaNome(mesChave) {
  if (!mesChave) return "maio 2026";
  if (String(mesChave).includes(" ")) return String(mesChave);

  const [ano, mes] = String(mesChave).split("-");
  const nomes = [
    "janeiro", "fevereiro", "março", "abril", "maio", "junho",
    "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
  ];

  const nomeMes = nomes[Number(mes) - 1] || "maio";
  return `${nomeMes} ${ano || "2026"}`;
}

function nomeArquivo(texto) {
  return String(texto || "relatorio")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getValorTransacao(t) {
  return Number(t?.valor || 0);
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="invexa-chart-tooltip">
      <strong>{label}</strong>
      {payload.map((item) => (
        <span key={item.dataKey || item.name} style={{ color: item.color }}>
          {item.name || item.dataKey}: {moeda(item.value)}
        </span>
      ))}
    </div>
  );
}

function Sparkline({ color = "#22c55e", data = [] }) {
  const chartData = data.length
    ? data.map((value, index) => ({ index, value }))
    : Array.from({ length: 22 }, (_, index) => ({
        index,
        value: 20 + Math.sin(index / 2) * 7 + index * 0.9 + (index % 3) * 2,
      }));

  return (
    <div className="kpi-sparkline">
      <ResponsiveContainer width="100%" height={46}>
        <AreaChart data={chartData} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`spark-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.55} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2.4}
            fill={`url(#spark-${color.replace("#", "")})`}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, note, tone, spark }) {
  const accent = KPI_COLORS[tone] || KPI_COLORS.entradas;

  return (
    <article className="power-kpi-card" style={{ "--accent": accent }}>
      <div className="power-kpi-glow" />
      <div className="power-kpi-head">
        <span className="power-kpi-icon"><Icon size={22} /></span>
        <div>
          <p>{label}</p>
          <strong>{value}</strong>
        </div>
      </div>
      <small>{note}</small>
      <Sparkline color={accent} data={spark} />
    </article>
  );
}

function InsightCard({ type, title, text, icon: Icon }) {
  return (
    <div className={`insight-card ${type}`}>
      <span><Icon size={25} /></span>
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}


function dataTransacao(t) {
  const data =
    t?.data?.toDate
      ? t.data.toDate()
      : t?.dataISO
      ? new Date(`${t.dataISO}T12:00:00`)
      : t?.data
      ? new Date(`${t.data}T12:00:00`)
      : null;

  if (!data || Number.isNaN(data.getTime())) return null;

  data.setHours(12, 0, 0, 0);
  return data;
}

function inicioSemanaSegunda(data) {
  const d = new Date(data);
  const diaSemana = d.getDay();
  const diferenca = diaSemana === 0 ? -6 : 1 - diaSemana;

  d.setDate(d.getDate() + diferenca);
  d.setHours(0, 0, 0, 0);

  return d;
}

function chaveData(data) {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

function obterPeriodoData(periodoTexto) {
  const texto = String(periodoTexto || "").trim().toLowerCase();

  if (/^\d{4}-\d{2}$/.test(texto)) {
    const [ano, mes] = texto.split("-");
    return { ano: Number(ano), mes: Number(mes) - 1 };
  }

  const meses = {
    janeiro: 0,
    fevereiro: 1,
    março: 2,
    marco: 2,
    abril: 3,
    maio: 4,
    junho: 5,
    julho: 6,
    agosto: 7,
    setembro: 8,
    outubro: 9,
    novembro: 10,
    dezembro: 11,
  };

  const partes = texto.split(/\s+/);
  const mes = meses[partes[0]] ?? new Date().getMonth();
  const ano = Number(partes[1]) || new Date().getFullYear();

  return { ano, mes };
}

function montarSemanasDoPeriodo(periodoTexto) {
  const { ano, mes } = obterPeriodoData(periodoTexto);
  const primeiroDiaMes = new Date(ano, mes, 1, 12);
  const ultimoDiaMes = new Date(ano, mes + 1, 0, 12);
  const primeiroInicioSemana = inicioSemanaSegunda(primeiroDiaMes);
  const semanas = [];
  const cursor = new Date(primeiroInicioSemana);
  let indice = 1;

  while (cursor <= ultimoDiaMes) {
    const inicio = new Date(cursor);
    const fim = new Date(cursor);
    fim.setDate(fim.getDate() + 6);

    semanas.push({
      chave: chaveData(inicio),
      semana: `Sem ${indice}`,
      periodo: `${inicio.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} - ${fim.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}`,
      inicio,
      fim,
      entradas: 0,
      saidas: 0,
      saldo: 0,
      investimentos: 0,
    });

    cursor.setDate(cursor.getDate() + 7);
    indice += 1;
  }

  return semanas;
}

export default function RelatoriosPage({ usuario }) {
  const reportRef = useRef(null);
  const [periodo, setPeriodo] = useState("maio 2026");
  const [resumo, setResumo] = useState({ entradas: 0, saidas: 0, saldo: 0 });
  const [ultimasTransacoes, setUltimasTransacoes] = useState([]);
  const [topCategorias, setTopCategorias] = useState([]);
  const [investimentos, setInvestimentos] = useState({ total: 0 });
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    if (!usuario?.uid) return;

    async function fetchData() {
      const [resumoData, transacoesData, categoriasData, investimentosData] =
        await Promise.all([
          getResumoPeriodo(usuario.uid, periodo),
          getUltimasTransacoes(usuario.uid, periodo),
          getTopCategorias(usuario.uid, periodo),
          getInvestimentosPeriodo(usuario.uid, periodo),
        ]);

      setResumo(resumoData || { entradas: 0, saidas: 0, saldo: 0 });
      setUltimasTransacoes(Array.isArray(transacoesData) ? transacoesData : []);
      setTopCategorias(Array.isArray(categoriasData) ? categoriasData : []);
      setInvestimentos(investimentosData || { total: 0 });
    }

    fetchData().catch(console.error);
  }, [usuario, periodo]);

  const periodoNome = mesParaNome(periodo);
  const agora = new Date();
  const geradoEm = agora.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const entradas = Number(resumo.entradas || 0);
  const saidas = Number(resumo.saidas || 0);
  const saldo = Number(resumo.saldo || 0);
  const investido = Number(investimentos.total || 0);
  const totalMovimentado = entradas + saidas;
  const taxaPoupanca = entradas > 0 ? (saldo / entradas) * 100 : 0;
  const mediaEntradas = entradas / Math.max(ultimasTransacoes.filter((t) => t.tipo === "receita").length, 1);
  const mediaSaidas = saidas / Math.max(ultimasTransacoes.filter((t) => t.tipo === "despesa").length, 1);
  const maiorEntrada = Math.max(0, ...ultimasTransacoes.filter((t) => t.tipo === "receita").map((t) => Number(t.valor || 0)));
  const maiorSaida = Math.max(0, ...ultimasTransacoes.filter((t) => t.tipo === "despesa").map((t) => Number(t.valor || 0)));

  const categoriasChart = useMemo(() => {
    if (topCategorias.length > 0) return topCategorias.slice(0, 6);
    return [
      { nome: "Moradia", valor: Math.max(saidas * 0.32, 1) },
      { nome: "Alimentação", valor: Math.max(saidas * 0.22, 1) },
      { nome: "Transporte", valor: Math.max(saidas * 0.15, 1) },
      { nome: "Lazer", valor: Math.max(saidas * 0.12, 1) },
      { nome: "Saúde", valor: Math.max(saidas * 0.09, 1) },
      { nome: "Outros", valor: Math.max(saidas * 0.1, 1) },
    ];
  }, [topCategorias, saidas]);

  const semanasChart = useMemo(() => {
    const semanas = montarSemanasDoPeriodo(periodo);
    const semanasPorChave = semanas.reduce((acc, semana) => {
      acc[semana.chave] = semana;
      return acc;
    }, {});

    ultimasTransacoes.forEach((t) => {
      const data = dataTransacao(t);
      if (!data) return;

      const inicio = inicioSemanaSegunda(data);
      const chave = chaveData(inicio);
      const semana = semanasPorChave[chave];
      if (!semana) return;

      const valor = getValorTransacao(t);
      if (t.tipo === "receita") semana.entradas += valor;
      if (t.tipo === "despesa") semana.saidas += valor;
    });

    const totalSemanas = Math.max(semanas.length, 1);

    return semanas.map((semana, index) => ({
      ...semana,
      saldo: Number((semana.entradas - semana.saidas).toFixed(2)),
      investimentos: Number(((investido / totalSemanas) * (index + 1)).toFixed(2)),
    }));
  }, [periodo, investido, ultimasTransacoes]);

  const sparkEntradas = semanasChart.map((item) => item.entradas || 0);
  const sparkSaidas = semanasChart.map((item) => item.saidas || 0);
  const sparkSaldo = semanasChart.map((item) => item.saldo || 0);
  const sparkInvest = semanasChart.map((item) => item.investimentos || 0);
  const sparkTaxa = semanasChart.map((item) => (item.entradas ? ((item.entradas - item.saidas) / item.entradas) * 100 : 0));

  async function exportarPDF() {
    if (!reportRef.current) return;
    setGerando(true);

    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: "#020916",
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png", 1);
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`relatorio_invexa_${nomeArquivo(periodoNome)}.pdf`);
    } finally {
      setGerando(false);
    }
  }

  const totalCat = categoriasChart.reduce((acc, item) => acc + Number(item.valor || 0), 0) || 1;
  const maiorCategoria = categoriasChart[0]?.nome || "Categoria";
  const maiorCategoriaPercentual = ((Number(categoriasChart[0]?.valor || 0) / totalCat) * 100).toFixed(1).replace(".", ",");

  return (
    <section className="invexa-report-premium-page">
      <div className="report-top-actions no-print">
        <div>
          <span>Plus › Relatórios</span>
          <h1>Relatórios</h1>
          <p>Dashboard financeiro premium com exportação em PDF.</p>
        </div>

        <div className="report-actions-right">
          <label className="period-input-pill">
            <CalendarDays size={17} />
            <input value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
          </label>
          <button type="button" onClick={exportarPDF} disabled={gerando}>
            <Download size={18} />
            {gerando ? "Gerando PDF..." : "Exportar PDF"}
          </button>
        </div>
      </div>

      <div className="powerbi-export-board" ref={reportRef}>
        <main className="powerbi-main">
          <header className="powerbi-header">
            <div>
              <h2>DASHBOARD FINANCEIRO</h2>
              <p>Visão geral completa da sua vida financeira</p>
            </div>
            <div className="header-meta">
              <div className="period-box">
                <span>Período Selecionado</span>
                <strong><CalendarDays size={17} /> {periodoNome}</strong>
              </div>
              <div className="generated-box">
                <CalendarDays size={24} />
                <div>
                  <span>Relatório gerado em</span>
                  <strong>{geradoEm}</strong>
                </div>
              </div>
            </div>
          </header>

          <div className="power-kpis">
            <KpiCard
              icon={CircleDollarSign}
              label="Entradas"
              value={moeda(entradas)}
              note="▲ +18,6% vs mês anterior"
              tone="entradas"
              spark={sparkEntradas}
            />
            <KpiCard
              icon={ArrowDownCircle}
              label="Saídas"
              value={moeda(saidas)}
              note="▲ +9,4% vs mês anterior"
              tone="saidas"
              spark={sparkSaidas}
            />
            <KpiCard
              icon={WalletCards}
              label="Saldo"
              value={moeda(saldo)}
              note="▲ +32,1% vs mês anterior"
              tone="saldo"
              spark={sparkSaldo}
            />
            <KpiCard
              icon={TrendingUp}
              label="Total investido"
              value={moeda(investido)}
              note="▲ +25,7% vs mês anterior"
              tone="investimentos"
              spark={sparkInvest}
            />
            <KpiCard
              icon={Home}
              label="Taxa poupança"
              value={`${taxaPoupanca.toFixed(1).replace(".", ",")}%`}
              note="▲ +6,8 p.p. vs mês anterior"
              tone="poupanca"
              spark={sparkTaxa}
            />
          </div>

          <div className="mobile-report-overview">
            <article className="mobile-main-balance">
              <span>Saldo disponível</span>
              <strong>{moeda(saldo)}</strong>
              <small>{saldo >= 0 ? "Resultado positivo no período" : "Atenção: saldo negativo no período"}</small>
            </article>

            <div className="mobile-mini-stats">
              <div>
                <span>Total movimentado</span>
                <strong>{moeda(totalMovimentado)}</strong>
              </div>
              <div>
                <span>Transações</span>
                <strong>{ultimasTransacoes.length}</strong>
              </div>
              <div>
                <span>Maior categoria</span>
                <strong>{maiorCategoria}</strong>
              </div>
              <div>
                <span>Taxa poupança</span>
                <strong>{`${taxaPoupanca.toFixed(1).replace(".", ",")}%`}</strong>
              </div>
            </div>
          </div>

          <div className="power-grid-row first-row">
            <section className="power-panel donut-panel">
              <h3>Distribuição por categoria (saídas)</h3>
              <div className="donut-content">
                <ResponsiveContainer width="58%" height={225}>
                  <PieChart>
                    <Pie
                      data={categoriasChart}
                      dataKey="valor"
                      nameKey="nome"
                      innerRadius={64}
                      outerRadius={105}
                      paddingAngle={1}
                      isAnimationActive={false}
                    >
                      {categoriasChart.map((entry, index) => (
                        <Cell key={entry.nome || index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="premium-legend">
                  {categoriasChart.map((item, index) => (
                    <div key={item.nome || index}>
                      <i style={{ background: COLORS[index % COLORS.length] }} />
                      <span>{item.nome}</span>
                      <strong>{((Number(item.valor || 0) / totalCat) * 100).toFixed(1).replace(".", ",")}%</strong>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="power-panel line-panel">
              <h3>Evolução do saldo</h3>
              <ResponsiveContainer width="100%" height={245}>
                <AreaChart data={semanasChart} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="saldoGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.72} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(148,163,184,.15)" vertical={false} />
                  <XAxis dataKey="semana" stroke="#cbd5e1" tickLine={false} axisLine={false} />
                  <YAxis stroke="#cbd5e1" tickLine={false} axisLine={false} tickFormatter={(v) => `R$ ${numero(v)}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="saldo" name="Saldo" stroke="#60a5fa" strokeWidth={3} fill="url(#saldoGradient)" dot={{ r: 4, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </section>

            <section className="power-panel bar-panel">
              <h3>Entradas x saídas</h3>
              <ResponsiveContainer width="100%" height={245}>
                <BarChart data={semanasChart} margin={{ top: 18, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(148,163,184,.13)" vertical={false} />
                  <XAxis dataKey="semana" stroke="#cbd5e1" tickLine={false} axisLine={false} />
                  <YAxis stroke="#cbd5e1" tickLine={false} axisLine={false} tickFormatter={(v) => `R$ ${numero(v)}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="entradas" name="Entradas" fill="#22c55e" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                  <Bar dataKey="saidas" name="Saídas" fill="#ef4444" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </section>
          </div>

          <div className="power-grid-row second-row">
            <section className="power-panel invest-panel">
              <h3>Investimentos (evolução)</h3>
              <ResponsiveContainer width="100%" height={226}>
                <AreaChart data={semanasChart} margin={{ top: 15, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="investGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#c084fc" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#c084fc" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(148,163,184,.15)" vertical={false} />
                  <XAxis dataKey="semana" stroke="#cbd5e1" tickLine={false} axisLine={false} />
                  <YAxis stroke="#cbd5e1" tickLine={false} axisLine={false} tickFormatter={(v) => `R$ ${numero(v)}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="investimentos" name="Investimentos" stroke="#d946ef" strokeWidth={3} fill="url(#investGradient)" dot={{ r: 4, fill: "#c084fc", stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </section>

            <section className="power-panel summary-panel">
              <h3>Resumo do período</h3>
              <div className="summary-list">
                <div><LineChart size={18} /><span>Total movimentado</span><strong>{moeda(totalMovimentado)}</strong></div>
                <div><ArrowUpCircle size={18} /><span>Média diária (entradas)</span><strong>{moeda(mediaEntradas)}</strong></div>
                <div><ArrowDownCircle size={18} /><span>Média diária (saídas)</span><strong>{moeda(mediaSaidas)}</strong></div>
                <div><Landmark size={18} /><span>Maior entrada</span><strong>{moeda(maiorEntrada)}</strong></div>
                <div><Wallet size={18} /><span>Maior saída</span><strong>{moeda(maiorSaida)}</strong></div>
                <div><BarChart3 size={18} /><span>Transações</span><strong>{ultimasTransacoes.length}</strong></div>
              </div>
            </section>

            <section className="power-panel highlights-panel">
              <h3>Destaques</h3>
              <InsightCard
                type="success"
                icon={CheckCircle2}
                title="Ótimo!"
                text={taxaPoupanca >= 20 ? "Sua taxa de poupança está acima da média. Continue assim!" : "Você já tem uma visão clara do período. Continue acompanhando."}
              />
              <InsightCard
                type="warning"
                icon={Info}
                title="Atenção"
                text={`Gastos com ${maiorCategoria} representam ${maiorCategoriaPercentual}% do total analisado.`}
              />
              <InsightCard
                type="tip"
                icon={ShieldCheck}
                title="Dica"
                text="Que tal investir um pouco mais? Sua reserva pode crescer ainda mais."
              />
            </section>
          </div>
        </main>
      </div>
    </section>
  );
}
